import { useId } from "react";

import { statusColor } from "@/components/lims";

const SIZE = 210;
const OUTER = 82;
const INNER = 50;

export function normalizeStatusRows(rows = []) {
  return rows
    .map((row) => ({ name: String(row?.name || "Unknown"), value: Number(row?.value || 0) }))
    .filter((row) => row.value > 0);
}

export function statusChartPaddingAngle(rows = []) {
  return normalizeStatusRows(rows).length > 1 ? 2 : 0;
}

export function statusChartAccessibleLabel(rows = []) {
  const normalized = normalizeStatusRows(rows);
  if (normalized.length === 0) return "Sample status chart. No samples match the current filters.";
  return `Sample status chart. ${normalized.map((row) => `${row.name}: ${row.value}`).join("; ")}.`;
}



/** A point on a circle, measured from twelve o'clock rather than three. */
function point(radius, degrees) {
  const radians = ((degrees - 90) * Math.PI) / 180;
  return [SIZE / 2 + radius * Math.cos(radians), SIZE / 2 + radius * Math.sin(radians)];
}

function arc(startAngle, endAngle) {
  const [x1, y1] = point(OUTER, startAngle);
  const [x2, y2] = point(OUTER, endAngle);
  const [x3, y3] = point(INNER, endAngle);
  const [x4, y4] = point(INNER, startAngle);
  const wide = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${OUTER} ${OUTER} 0 ${wide} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${INNER} ${INNER} 0 ${wide} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

/**
 * The ring's segments, in drawing order.
 *
 * A laboratory whose samples are all in one state is the ordinary case early
 * on, and a single segment spanning the whole circle has no arc to draw: its
 * start and end land on the same point and the path collapses to nothing. That
 * case is drawn as a plain ring instead, so a single-status laboratory sees a
 * complete donut rather than a rendering fault.
 */
export function statusChartSegments(rows = []) {
  const normalized = normalizeStatusRows(rows);
  const total = normalized.reduce((sum, row) => sum + row.value, 0);
  if (total <= 0) return [];
  if (normalized.length === 1) {
    return [{ ...normalized[0], ring: true, fill: statusColor(normalized[0].name) }];
  }

  const gap = statusChartPaddingAngle(normalized);
  let cursor = 0;
  return normalized.map((row) => {
    const sweep = (row.value / total) * 360;
    const segment = {
      ...row,
      fill: statusColor(row.name),
      d: arc(cursor + gap / 2, cursor + sweep - gap / 2),
    };
    cursor += sweep;
    return segment;
  });
}

/**
 * Sample status as a donut, drawn directly rather than through Recharts.
 *
 * Recharts built the chart's layer structure and then drew no sector at all
 * under React 19. Its container reported width(-1), and supplying explicit
 * pixel dimensions did not help either, so the donut rendered as blank space
 * while the table beside it listed the same figures correctly. Five arcs of
 * SVG are less code than the workarounds were, always render, and take their
 * colors straight from the status tokens.
 */
export default function SampleStatusChart({ rows = [] }) {
  const normalized = normalizeStatusRows(rows);
  const segments = statusChartSegments(rows);
  const tableId = `sample-status-data-${useId().replace(/:/g, "")}`;

  return (
    <figure className="mt-3 grid grid-cols-1 items-center gap-4">
      <div
        role="img"
        tabIndex={0}
        aria-label={statusChartAccessibleLabel(normalized)}
        aria-describedby={tableId}
        data-testid="sample-status-chart"
        className="flex h-[210px] min-w-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
      >
        {segments.length > 0 ? (
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
            {segments.map((segment) => (segment.ring ? (
              <circle
                key={segment.name}
                data-testid={`status-segment-${segment.name}`}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={(OUTER + INNER) / 2}
                fill="none"
                stroke={segment.fill}
                strokeWidth={OUTER - INNER}
              />
            ) : (
              <path
                key={segment.name}
                data-testid={`status-segment-${segment.name}`}
                d={segment.d}
                fill={segment.fill}
              />
            )))}
          </svg>
        ) : (
          <span className="text-sm text-slate-500">No samples match the current filters.</span>
        )}
      </div>

      <div className="max-h-[210px] overflow-auto rounded-md border border-slate-200">
        <table id={tableId} data-testid="sample-status-data-table" className="w-full text-sm">
          <caption className="sr-only">Data table for sample status chart</caption>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-semibold">Status</th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {normalized.length > 0 ? normalized.map((row) => (
              <tr key={row.name}>
                <th scope="row" className="px-3 py-2 text-left font-medium text-slate-700">
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: statusColor(row.name) }}
                    />
                    {row.name}
                  </span>
                </th>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-700">{row.value}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={2} className="px-3 py-4 text-center text-slate-500">No data</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
